import { Link } from 'react-router-dom';
import type { Work } from '@/mocks/movies';
import { formatViews } from '@/utils/content';

interface WorkCardProps {
  work: Work;
  showSynopsis?: boolean;
}

export default function WorkCard({ work, showSynopsis = false }: WorkCardProps) {
  return (
    <Link
      to={`/detail/${work.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative w-full overflow-hidden bg-background-200 pt-[140%]">
        <img
          src={work.cover}
          alt={`${work.title} 海报`}
          title={`${work.title} ${work.genres.join(' ')}`}
          className="absolute inset-0 h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-md bg-primary-950/80 px-2 py-0.5 text-xs font-medium text-background-50 backdrop-blur-sm">
          {work.typeLabel}
        </span>
        {work.rating > 0 && (
          <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-primary-950">
            <i className="ri-star-fill text-[10px]" aria-hidden="true" />
            {work.rating.toFixed(1)}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-gradient-to-t from-primary-950/90 to-transparent px-2.5 pb-2 pt-6 text-[11px] text-background-200">
          <i className="ri-eye-line" aria-hidden="true" />
          <span>{formatViews(work.views)} 次浏览</span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-3">
        <h3 className="font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
          {work.title}
        </h3>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-secondary-500">
          <span>{work.year}</span>
          <span className="h-1 w-1 rounded-full bg-secondary-300" aria-hidden="true" />
          <span>{work.region}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {work.genres.slice(0, 3).map((genre) => (
            <span key={genre} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
              {genre}
            </span>
          ))}
        </div>
        {showSynopsis && (
          <p className="mt-2.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">{work.synopsis}</p>
        )}
      </div>
    </Link>
  );
}